// FWAA Business Exchange — master board.
// Company "buckets" with KPIs (attendees / properties / Apollo accounts),
// the claim board (who claimed who, what's open), and admin re-import.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { ClaimBoard, ClaimSummary } from '../components/FwaaShared';

const SORTS = [
  { key: 'props', label: 'Total Properties' },
  { key: 'apollo', label: 'Apollo Accounts' },
  { key: 'attendees', label: 'Attendees' },
  { key: 'name', label: 'Name (A→Z)' },
];

export default function FwaaOverviewPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [sortKey, setSortKey] = useState('props');
  const [showBoard, setShowBoard] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState('');
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.getFwaaOverview()
      .then(d => { setData(d); setError(''); })
      .catch(err => setError(err?.responseBody?.error || err.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function onImportFile(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (!window.confirm(`Re-import "${file.name}"? This replaces the whole board (claims are kept where the attendee still exists).`)) return;
    setImporting(true); setImportMsg('');
    try {
      const res = await api.importFwaa(file);
      const s = res.summary || {};
      setImportMsg(`Imported ${s.companies} companies, ${s.attendees} attendees, ${s.properties} properties. Claims kept: ${s.claims_kept}.`);
      load();
    } catch (err) {
      setImportMsg(err?.responseBody?.error || err.message || 'Import failed');
    } finally { setImporting(false); }
  }

  const companies = data?.companies || [];
  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    let list = companies;
    if (q) list = list.filter(c => (c.name + ' ' + (c.aln_name || '')).toLowerCase().includes(q));
    const cmp = {
      props: (a, b) => b.property_count - a.property_count,
      apollo: (a, b) => b.apollo_count - a.apollo_count,
      attendees: (a, b) => b.attendee_count - a.attendee_count,
      name: (a, b) => a.name.localeCompare(b.name),
    }[sortKey];
    return list.slice().sort(cmp);
  }, [companies, filter, sortKey]);

  const totals = data?.totals;
  const board = data?.claim_board;
  const openCount = board ? (data.totals.attendees - board.total_claimed) : null;

  return (
    <div className="directory-page fwaa-page">
      <div className="fwaa-hero">
        <div>
          <h1 className="fwaa-hero__title">FWAA Business Exchange</h1>
          <p className="fwaa-hero__sub">
            Aug 6, 2026 · {loading ? 'Loading...' : totals
              ? `${totals.companies} management companies · ${totals.attendees} attendees · ${totals.properties.toLocaleString('en-US')} properties · ${totals.apollo_accounts} Apollo accounts`
              : ''}
          </p>
        </div>
        <div className="fwaa-hero__actions">
          <Link to="/sales/fwaa/my" className="fwaa-my-btn">
            My Appointments{board && board.my_count > 0 ? ` (${board.my_count})` : ''}
          </Link>
          {data?.can_import ? (
            <>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={onImportFile} />
              <button type="button" className="btn btn-sm" onClick={() => fileRef.current && fileRef.current.click()} disabled={importing}>
                {importing ? 'Importing...' : 'Re-import Spreadsheet'}
              </button>
            </>
          ) : null}
        </div>
      </div>
      {importMsg ? <div className="fwaa-import-msg">{importMsg}</div> : null}
      {error ? <div className="card" style={{ padding: 16, color: '#dc2626' }}>{error}</div> : null}

      {board ? (
        <div className="fwaa-claim-panel">
          <div className="fwaa-claim-panel__head">
            <ClaimSummary board={board} />
            <div className="fwaa-claim-panel__right">
              {openCount !== null ? <span className="fwaa-open-count">{openCount} attendees open</span> : null}
              <button type="button" className="fwaa-linklike" onClick={() => setShowBoard(v => !v)}>
                {showBoard ? 'Hide claim board' : 'Show claim board'}
              </button>
            </div>
          </div>
          {showBoard ? <ClaimBoard board={board} myUserId={user?.id} /> : null}
        </div>
      ) : null}

      <div className="directory-companies__controls">
        <input
          type="search"
          className="directory-companies__filter"
          placeholder="Filter companies..."
          value={filter}
          onChange={e => setFilter(e.target.value)}
        />
        <label className="directory-companies__sort">
          <span className="directory-companies__sort-label">Sort by</span>
          <select className="directory-companies__sort-select" value={sortKey} onChange={e => setSortKey(e.target.value)}>
            {SORTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </label>
      </div>

      <div className="directory-company-grid">
        {visible.map(c => <Bucket key={c.id} company={c} />)}
        {!loading && visible.length === 0 ? (
          <div className="directory-companies__empty">No companies match &ldquo;{filter}&rdquo;.</div>
        ) : null}
      </div>
    </div>
  );
}

function Bucket({ company }) {
  return (
    <Link to={`/sales/fwaa/companies/${company.id}`} className="directory-company-card fwaa-bucket">
      <div className="fwaa-bucket__top">
        <h3 className="directory-company-card__name">{company.name}</h3>
        {company.claimed_count > 0 ? (
          <span className="fwaa-bucket__claimed">{company.claimed_count} claimed</span>
        ) : null}
      </div>
      {company.aln_name && company.aln_name !== company.name ? (
        <div className="fwaa-bucket__aln">{company.aln_name}</div>
      ) : null}
      <div className="directory-company-card__kpis">
        <Kpi value={company.attendee_count} label="Attendees" accent="blue" />
        <Kpi value={company.property_count} label="Properties" />
        <Kpi value={company.apollo_count} label="Apollo" accent={company.apollo_count > 0 ? 'green' : null} />
        <Kpi value={company.props_under_attendees} label="Props w/ Attendee" accent={company.props_under_attendees > 0 ? 'amber' : null} />
      </div>
    </Link>
  );
}

function Kpi({ value, label, accent }) {
  const cls = 'directory-company-card__kpi' + (accent ? ` directory-company-card__kpi--${accent}` : '');
  return (
    <div className={cls}>
      <div className="directory-company-card__kpi-value">{value}</div>
      <div className="directory-company-card__kpi-label">{label}</div>
    </div>
  );
}
